import { Box, AppBar, Toolbar, Button, IconButton, Drawer, List, ListItem } from '@mui/material';
import { Routes, Route, Link as RouterLink, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Home from './pages/Home';
import Support from './pages/Support';
import Builders from './pages/Builders';
import ProductsAndServices from './pages/ProductsAndServices';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import Enquiry from './pages/Enquiry';
import Onboarding from './pages/Onboarding';
import Footer from './Footer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import TermsAndConditions from './pages/TermsAndConditions';

// Every route change lands at the top of the new page. Without this the browser keeps
// the previous scroll offset, so a page opens half way down.
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Browser tab title and search description for each route. Google shows these in
// results, so every page gets its own rather than the one default in index.html.
const PAGE_META: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Hallidays Heating and Cooling | Heating & Cooling for Victorian Builders',
    description: "Supplying and installing heating and cooling for Victoria's leading home builders since 1969.",
  },
  '/builders': {
    title: 'Builders | Hallidays Heating and Cooling',
    description: 'We partner with Victorian builders from concept to completion: design, project management, rough-in and fit-off.',
  },
  '/products-and-services': {
    title: 'Products & Services | Hallidays Heating and Cooling',
    description: 'Ducted heating, reverse cycle, evaporative cooling, split systems and panel heaters from leading brands.',
  },
  '/onboarding': {
    title: 'Welcome Hub | Hallidays Heating and Cooling',
    description: 'New home? How to use and care for your heating and cooling system, controller manuals and warranty information.',
  },
  '/support': {
    title: 'Support & Warranty | Hallidays Heating and Cooling',
    description: 'Help with your Hallidays-installed system: AI assistant, FAQs and online support request.',
  },
  '/enquiry': {
    title: 'Contact Us | Hallidays Heating and Cooling',
    description: 'Contact Hallidays in Dandenong South or Kialla for quotes, builder enquiries and general questions.',
  },
  '/terms': {
    title: 'Terms of Trade - Hallidays Heating and Cooling',
    description: 'Hallidays Heating and Cooling terms of trade for builders, businesses and homeowners.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy - Hallidays Heating and Cooling',
    description: 'How Hallidays collects, uses and protects personal and credit information.',
  },
  '/cookies-policy': {
    title: 'Cookie Policy - Hallidays Heating and Cooling',
    description: 'How the Hallidays website uses cookies and similar technologies.',
  },
};

const PageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = PAGE_META[pathname];
    if (!meta) return;
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
  }, [pathname]);

  return null;
};

type MenuItem = {
  path: string;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
};

// The onboarding guide frames a document that brings its own footer, so the app's
// is left off underneath it. Everything else on the route is ordinary app chrome.
const ONBOARDING_PATH = '/onboarding';

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // /our-story has no route of its own — the story section lives on the home page.
    // Older links and the static welcome page still point at it, so send them to the
    // top of home rather than scrolling down into the section.
    if (location.pathname === '/our-story') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const menuItems: MenuItem[] = [
    // Goes to the top of the home page, not to the story section further down.
    { path: '/', label: 'Our Story' },
    { path: '/builders', label: 'Builders' },
    { path: '/products-and-services', label: 'Products & Services' },
    { path: ONBOARDING_PATH, label: 'Welcome Hub' }
  ];

  const handleMobileMenuClick = (item: MenuItem, e?: React.MouseEvent) => {
    if (item.onClick) {
      item.onClick(e as React.MouseEvent);
    } else {
      navigate(item.path);
      window.scrollTo(0, 0); // Add scroll to top for mobile navigation
    }
    setMobileMenuOpen(false);
  };

  // Handle navigation with scroll reset
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: 0,
      bgcolor: '#f8fbff',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    }}>
      {/* Add ScrollToTop component here */}
      <ScrollToTop />
      <PageMeta />
      
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(0, 102, 178, 0.08)',
          width: '100%',
        }}
      >
        <Toolbar 
          sx={{ 
            minHeight: { xs: '64px', sm: '72px' },
            px: { xs: 2, sm: 4, md: 5 },
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            onClick={() => window.scrollTo(0, 0)} // Add scroll reset for logo click
            sx={{
              // flex: 1 here and on the buttons box keeps the nav links
              // optically centred in the toolbar regardless of their width
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              mr: { xs: 2, sm: 4 },
            }}
          >
            <img 
              src="/hallidays.png" 
              alt="Hallidays Logo"
              style={{ 
                height: '32px',
                width: 'auto',
              }}
            />
          </Box>

          {/* Desktop Menu */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            gap: 3
          }}>
            {menuItems.map((item) => {
              const active = location.pathname === item.path;
              const linkStyle: React.CSSProperties = {
                color: active ? '#0066b2' : '#445668',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: active ? 600 : 500,
                position: 'relative',
                whiteSpace: 'nowrap',
              };
              const onMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.color = '#0066b2';
              };
              const onMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.color = active ? '#0066b2' : '#445668';
              };

              return (
                <RouterLink
                  key={item.path}
                  to={item.path}
                  onClick={(e) => {
                    if (item.onClick) {
                      item.onClick(e as React.MouseEvent);
                    } else {
                      // Force scroll reset for standard navigation
                      e.preventDefault();
                      handleNavigation(item.path);
                    }
                  }}
                  style={linkStyle}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                >
                  {item.label}
                </RouterLink>
              );
            })}
          </Box>
          
          {/* Get Help & Contact Us Buttons */}
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, gap: 1.25, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              component={RouterLink}
              to="/support"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/support');
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                py: 0.75,
                px: 2,
                color: '#0066b2',
                fontSize: '0.9rem',
                fontWeight: 500,
                border: '1px solid',
                borderColor: 'rgba(0, 102, 178, 0.15)',
                backgroundColor: 'transparent',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0066b2 0%, #004c8c 100%)',
                  borderColor: 'transparent',
                  color: '#ffffff',
                  boxShadow: '0 4px 8px rgba(0, 102, 178, 0.15)',
                },
                '&:active': {
                  boxShadow: '0 2px 4px rgba(0, 102, 178, 0.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Support & Warranty
            </Button>
            <Button
              component={RouterLink}
              to="/enquiry"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/enquiry');
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                py: 0.75,
                px: 2.25,
                color: '#ffffff',
                fontSize: '0.9rem',
                fontWeight: 500,
                background: 'linear-gradient(135deg, #0066b2 0%, #004c8c 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #004c8c 0%, #003666 100%)',
                  boxShadow: '0 4px 8px rgba(0, 102, 178, 0.15)',
                },
                '&:active': {
                  boxShadow: '0 2px 4px rgba(0, 102, 178, 0.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Contact Us
            </Button>
          </Box>
          
          {/* Mobile Menu Button */}
          <IconButton
            sx={{ 
              display: { xs: 'block', md: 'none' },
              ml: 'auto',
              mr: 1,
              color: '#445668'
            }}
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '300px',
            backgroundColor: '#ffffff',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {menuItems.map((item) => (
            <ListItem 
              key={item.path}
              onClick={(e) => handleMobileMenuClick(item, e)}
              sx={{
                py: 2,
                color: location.pathname === item.path ? '#0066b2' : '#445668',
                fontWeight: location.pathname === item.path ? 600 : 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 102, 178, 0.08)',
                  color: '#0066b2',
                }
              }}
            >
              {item.label}
            </ListItem>
          ))}
          <ListItem>
            <Button
              component={RouterLink}
              to="/support"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                navigate('/support');
                window.scrollTo(0, 0);
                setMobileMenuOpen(false);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                py: 1.5,
                color: '#ffffff',
                fontSize: '0.95rem',
                fontWeight: 500,
                backgroundColor: '#0066b2',
                '&:hover': {
                  backgroundColor: '#004c8c',
                },
              }}
            >
              Support & Warranty
            </Button>
          </ListItem>
          <ListItem>
            <Button
              component={RouterLink}
              to="/enquiry"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                navigate('/enquiry');
                window.scrollTo(0, 0);
                setMobileMenuOpen(false);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                py: 1.5,
                mt: 1,
                color: '#0066b2',
                fontSize: '0.95rem',
                fontWeight: 500,
                border: '1.5px solid',
                borderColor: 'rgba(0, 102, 178, 0.2)',
                '&:hover': {
                  borderColor: '#0066b2',
                  backgroundColor: 'rgba(0, 102, 178, 0.08)',
                },
              }}
            >
              Contact Us
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          width: '100%',
          flexGrow: 1,
          bgcolor: '#f8fbff',
        }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/builders" element={<Builders />} />
          <Route path="/products-and-services" element={<ProductsAndServices />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/enquiry" element={<Enquiry />} />
          <Route path={ONBOARDING_PATH} element={<Onboarding />} />
        </Routes>
      </Box>

      {location.pathname !== ONBOARDING_PATH && <Footer />}
    </Box>
  );
};

export default Layout; 